(() => {
  const TOKEN_PATTERN = /\[([a-z_]+)\]/g;

  class NuoCartMilestones extends HTMLElement {
    constructor() {
      super();
      this.onCartChanged = this.onCartChanged.bind(this);
      this.scheduleTimer = null;
    }

    connectedCallback() {
      document.addEventListener("cart:change", this.onCartChanged);
      this.update();
      this.scheduleTimer = window.setInterval(() => this.update(), 30000);
    }

    disconnectedCallback() {
      document.removeEventListener("cart:change", this.onCartChanged);
      window.clearInterval(this.scheduleTimer);
    }

    static get observedAttributes() {
      return ["total-price"];
    }

    attributeChangedCallback() {
      if (this.isConnected) {
        this.update();
      }
    }

    getNumber(name) {
      const value = Number.parseFloat(this.getAttribute(name));
      return Number.isFinite(value) ? value : 0;
    }

    getLocalMinute(date = new Date()) {
      const timeZone = this.getAttribute("time-zone") || "Europe/Bucharest";
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23"
      }).formatToParts(date).reduce((result, part) => {
        result[part.type] = part.value;
        return result;
      }, {});

      return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
    }

    normalizeLocalMinute(value) {
      return (value || "").trim().replace(" ", "T").slice(0, 16);
    }

    isCampaignActive() {
      if (window.Shopify?.designMode || new URLSearchParams(window.location.search).get("campaignWidgetQa") === "active") {
        return true;
      }

      try {
        const now = this.getLocalMinute();
        const start = this.normalizeLocalMinute(this.getAttribute("start-at"));
        const end = this.normalizeLocalMinute(this.getAttribute("end-at"));
        return (!start || now >= start) && (!end || now <= end);
      } catch (error) {
        console.warn("NUO cart milestones: invalid campaign schedule", error);
        return false;
      }
    }

    formatMoney(cents) {
      const locale = this.getAttribute("number-locale") || Shopify.locale || "ro-RO";
      const currencyLabel = this.getAttribute("currency-label") || "lei";
      const value = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Math.max(0, cents) / 100);

      return `${value} ${currencyLabel}`;
    }

    merge(template, values) {
      return (template || "").replace(TOKEN_PATTERN, (match, key) => values[key] ?? match);
    }

    renderMessage(element, template, values, emphasizeRewards) {
      const source = template || "";
      const fragment = document.createDocumentFragment();
      let lastIndex = 0;

      source.replace(TOKEN_PATTERN, (match, key, offset) => {
        fragment.append(document.createTextNode(source.slice(lastIndex, offset)));

        const value = values[key] ?? match;

        if (emphasizeRewards && (key === "shipping_reward" || key === "giveaway_reward")) {
          const reward = document.createElement("strong");
          reward.className = "nuo-cart-milestones__reward";
          reward.textContent = value;
          fragment.append(reward);
        } else {
          fragment.append(document.createTextNode(value));
        }

        lastIndex = offset + match.length;
        return match;
      });

      fragment.append(document.createTextNode(source.slice(lastIndex)));
      element.replaceChildren(fragment);
    }

    updateVisibility(active) {
      const wrapper = this.closest("[data-nuo-cart-progress-switcher]");
      const fallback = wrapper?.querySelector("[data-nuo-cart-progress-fallback]");
      this.hidden = !active;

      if (fallback) {
        fallback.hidden = active;
      }
    }

    update() {
      const active = this.isCampaignActive();
      this.updateVisibility(active);

      if (!active) {
        return;
      }

      const totalPrice = this.getNumber("total-price");
      const shippingThreshold = this.getNumber("shipping-threshold");
      const giveawayThreshold = this.getNumber("giveaway-threshold");
      const shippingReward = this.getAttribute("shipping-reward") || "Transport gratuit";
      const giveawayReward = this.getAttribute("giveaway-reward") || "Mega Giveaway";
      const milestones = [
        { key: "shipping", threshold: shippingThreshold, reward: shippingReward },
        { key: "giveaway", threshold: giveawayThreshold, reward: giveawayReward }
      ];
      const lockedMilestones = milestones.filter((milestone) => totalPrice < milestone.threshold);
      const nextThreshold = lockedMilestones.length
        ? Math.min(...lockedMilestones.map((milestone) => milestone.threshold))
        : Math.max(shippingThreshold, giveawayThreshold);
      const nextRewards = lockedMilestones
        .filter((milestone) => milestone.threshold === nextThreshold)
        .map((milestone) => milestone.reward);
      const maximumThreshold = Math.max(shippingThreshold, giveawayThreshold, 1);
      const progress = Math.min(totalPrice / maximumThreshold, 1);
      const isComplete = lockedMilestones.length === 0;
      const messageTemplate = !isComplete
        ? this.getAttribute("message-pending")
        : this.getAttribute("message-complete");
      const values = {
        remaining_amount: this.formatMoney(nextThreshold - totalPrice),
        next_reward: nextRewards.join(" + "),
        shipping_threshold: this.formatMoney(shippingThreshold),
        giveaway_threshold: this.formatMoney(giveawayThreshold),
        shipping_reward: shippingReward,
        giveaway_reward: giveawayReward,
        currency: this.getAttribute("currency-label") || "lei"
      };

      this.style.setProperty("--nuo-cart-milestones-progress", progress);
      const message = this.querySelector("[data-nuo-cart-milestones-message]");

      if (message) {
        message.classList.toggle("is-complete", isComplete);
        this.renderMessage(message, messageTemplate, values, isComplete);
      }

      const progressBar = this.querySelector("[role='progressbar']");
      progressBar?.setAttribute("aria-valuenow", String(Math.round(progress * 100)));

      milestones.forEach((milestone) => {
        const item = this.querySelector(`[data-nuo-milestone='${milestone.key}']`);
        const isUnlocked = totalPrice >= milestone.threshold;
        const statusTemplate = this.getAttribute(isUnlocked ? "unlocked-label" : "locked-label");
        item?.classList.toggle("is-unlocked", isUnlocked);
        item?.querySelector("[data-nuo-milestone-status]")?.replaceChildren(
          this.merge(statusTemplate, { ...values, threshold: this.formatMoney(milestone.threshold) })
        );
      });
    }

    onCartChanged(event) {
      const cart = event.detail?.cart;

      if (!cart) {
        return;
      }

      const itemPrice = cart.items
        .filter((item) => item.requires_shipping)
        .reduce((sum, item) => sum + item.final_line_price, 0);
      const cartDiscount = cart.cart_level_discount_applications
        .reduce((sum, discount) => sum + discount.total_allocated_amount, 0);

      this.setAttribute("total-price", String(itemPrice - cartDiscount));
    }
  }

  if (!window.customElements.get("nuo-cart-milestones")) {
    window.customElements.define("nuo-cart-milestones", NuoCartMilestones);
  }
})();
